import { useQuery } from '@tanstack/react-query';
import type { TriageSuggestion } from '@syndiqia/agents/contracts';
import type { RequestDetail, UpdateRequest } from '@syndiqia/comms/contracts';
import { useAuth } from 'react-oidc-context';
import { useApi } from '../api';
import { useCommsMutation } from '../comms-queries';
import { devSubject } from '../config';
import { useI18n } from '../i18n';
import { useRegistryMutation } from '../queries';
import { useSyndicate } from '../session';

/** The triage agent's suggestion for a request, for the board: apply, use the draft, or dismiss. */
export function SuggestionPanel({ request, onUseDraft }: { request: RequestDetail; onUseDraft: (text: string) => void }) {
  const { t } = useI18n();
  const api = useApi();
  const s = useSyndicate();
  const auth = useAuth();
  const suggestions = useQuery({
    queryKey: [s.tenantId, 'comms', 'suggestions', request.id],
    queryFn: () => api<TriageSuggestion[]>(`${s.api}/agents/requests/${request.id}/suggestions`),
    refetchInterval: (q) => (q.state.data?.length ? false : 15_000), // the agent may still be working
  });
  const decide = useRegistryMutation<{ id: string; status: 'used' | 'dismissed' }, void>(
    (a, base, { id, status }) => a(`${base.replace(/\/registry$/, '/agents')}/suggestions/${id}`, { method: 'POST', body: { status } }),
    ['comms'],
  );
  const update = useCommsMutation<UpdateRequest, RequestDetail>((a, base, input) => a(`${base}/requests/${request.id}`, { method: 'PATCH', body: input }));

  const suggestion = suggestions.data?.[0];
  if (!suggestion) return null;
  if (suggestion.status === 'dismissed') return <p className="muted small">✦ {t.suggestion.dismissed}</p>;

  const openDocument = async (documentId: string) => {
    const tab = window.open('', '_blank');
    const res = await fetch(`${s.api}/documents/${documentId}/file`, {
      headers: { ...(auth.user?.access_token ? { Authorization: `Bearer ${auth.user.access_token}` } : {}), ...(devSubject ? { 'x-dev-subject': devSubject } : {}) },
    });
    if (!res.ok) return tab?.close();
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
  };

  return (
    <section className="panel suggestion-panel">
      <header className="dash-card-header">
        <h2>✦ {t.suggestion.title}</h2>
        <span className="muted small">{t.suggestion.confidence[suggestion.confidence]}</span>
      </header>
      <p className="muted small">{t.suggestion.subtitle}</p>

      <div className="suggestion-facts">
        {suggestion.category !== request.category && (
          <span>
            {t.suggestion.suggested} : <strong>{t.requests.categories[suggestion.category]}</strong>{' '}
            <button type="button" className="link-button small" onClick={() => update.mutate({ category: suggestion.category })}>
              {t.suggestion.apply}
            </button>
          </span>
        )}
        {suggestion.priority === 'urgent' && request.priority !== 'urgent' && (
          <span>
            {t.suggestion.urgent} — {suggestion.priorityReason}{' '}
            <button type="button" className="link-button small" onClick={() => update.mutate({ priority: 'urgent' })}>
              {t.suggestion.apply}
            </button>
          </span>
        )}
        {suggestion.needsBoardDecision && <span className="badge badge-approval-pending">{t.suggestion.boardDecision}</span>}
      </div>

      <h3>{t.suggestion.draft}</h3>
      <pre className="email-body suggestion-draft">{suggestion.reply}</pre>

      {suggestion.citations.length > 0 && (
        <>
          <h3>{t.suggestion.sources}</h3>
          <ul className="citations">
            {suggestion.citations.map((c) => (
              <li key={`${c.documentId}-${c.pageNo}`}>
                <button type="button" className="link-button small" onClick={() => void openDocument(c.documentId)}>
                  {c.title}, {t.suggestion.page(c.pageNo)}
                </button>
                <span className="muted small"> — « {c.quote} »</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {suggestion.internalNote && (
        <div className="message message-internal">
          <strong className="small">{t.suggestion.internalNote}</strong>
          <p className="message-body small">{suggestion.internalNote}</p>
        </div>
      )}

      {suggestion.status === 'proposed' ? (
        <div className="form-actions">
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              onUseDraft(suggestion.reply);
              decide.mutate({ id: suggestion.id, status: 'used' });
            }}
          >
            {t.suggestion.use}
          </button>
          <button type="button" className="button" onClick={() => decide.mutate({ id: suggestion.id, status: 'dismissed' })}>
            {t.suggestion.dismiss}
          </button>
        </div>
      ) : (
        <p className="muted small">{t.suggestion.used}</p>
      )}
    </section>
  );
}
